import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FlaskConical, Globe, Plug, SlidersHorizontal, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { canShapeSite } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import type { EnvironmentBody, SiteDetailBody } from '@/shared/api/generated/model';
import { sitesSetEnvironmentAddress, sitesUpdate } from '@/shared/api/generated/sites/sites';
import { ApiError } from '@/shared/api/http';
import { t } from '@/shared/i18n';
import { slugify } from '@/shared/lib/slugify';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { Textarea } from '@/shared/ui/Textarea';
import { toast } from '@/shared/ui/Toast';

import { ConnectSection } from './Connect';
import { FeaturesSection } from './FeaturesSection';
import styles from './SettingsPage.module.scss';
import { isSettingsTab } from './tabs';

/**
 * Настройки сайта — вкладками: общее (название, языки), адреса окружений и
 * подключение сайта (ключи, .env, шаги для разработчика).
 *
 * Окружений у сайта ровно два, и ни добавить, ни удалить их нельзя — здесь
 * только их адреса. Адрес — не домен хостинга: Faceta по нему открывает
 * предпросмотр и разрешает браузеру обращаться к API
 * (docs/research/04-environments.md).
 *
 * Экран общий для обоих окружений: устройство сайта одно, и переключатель
 * окружения здесь ничего не меняет. Вкладка — в адресе: ссылку на
 * «Подключение сайта» можно отправить разработчику.
 */
export function SettingsPage() {
  const scope = useSiteScope();
  const { tab = 'general' } = useSearch({ from: '/authed/sites/$siteId/$env/settings' });
  const navigate = useNavigate();

  if (scope === null) {
    return null;
  }

  if (!scope.loading && !canShapeSite(scope.role)) {
    return <EmptyState title={t.settings.title} description={t.settings.noAccess} />;
  }

  // С сужением «только Development» — только просмотр: адреса и название
  // общие для обоих окружений, их правку сервер не примет.
  const readOnly = !scope.canShared('site.shape');

  return (
    <div className={styles['page']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.settings.title}</h1>
        <p className={styles['section__text']}>
          Устройство сайта — общее для Production и Development.
        </p>
      </header>

      {readOnly && !scope.loading && <p className={styles['note']}>{t.env.devOnlyNote}</p>}

      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (isSettingsTab(value)) {
            void navigate({
              to: '/sites/$siteId/$env/settings',
              params: { siteId: scope.siteId, env: scope.env },
              search: value === 'general' ? {} : { tab: value },
              replace: true,
            });
          }
        }}
      >
        <TabsList>
          <TabsTrigger
            value="general"
            active={tab === 'general'}
            icon={<SlidersHorizontal size={15} />}
          >
            Общее
          </TabsTrigger>
          <TabsTrigger value="addresses" active={tab === 'addresses'} icon={<Globe size={15} />}>
            Адреса
          </TabsTrigger>
          <TabsTrigger value="connect" active={tab === 'connect'} icon={<Plug size={15} />}>
            Подключение
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className={styles['tab']}>
          {scope.site === undefined ? (
            <Skeleton height={180} />
          ) : (
            <>
              <GeneralSection key={scope.site.updated_at} site={scope.site} readOnly={readOnly} />
              <FeaturesSection
                key={`features:${scope.site.updated_at}`}
                site={scope.site}
                readOnly={readOnly}
              />
              <LanguagesSection
                key={`languages:${scope.site.updated_at}`}
                site={scope.site}
                readOnly={readOnly}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="addresses" className={styles['tab']}>
          <p className={styles['section__text']}>{t.settings.addressesText}</p>

          {scope.loading ? (
            <Skeleton height={140} />
          ) : (
            <div className={styles['grid']}>
              {scope.environments.map((environment) => (
                <AddressCard
                  key={`${environment.id}:${environment.updated_at}`}
                  siteId={scope.siteId}
                  environment={environment}
                  readOnly={readOnly}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="connect" className={styles['tab']}>
          <p className={styles['section__text']}>
            Сайт забирает содержимое из Faceta по ключу окружения. Здесь — ключи, готовые файлы .env
            и шаги для разработчика сайта.
          </p>

          {scope.loading ? (
            <Skeleton height={260} />
          ) : (
            <ConnectSection
              siteId={scope.siteId}
              environments={scope.environments}
              devOnly={scope.devOnly}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GeneralSection({ site, readOnly }: { site: SiteDetailBody; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(site.name);
  const [slug, setSlug] = useState(site.slug);
  const [slugError, setSlugError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const changed = name.trim() !== site.name || slug !== site.slug;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSlugError(undefined);
    setBusy(true);

    sitesUpdate(site.id, { name: name.trim(), slug }, { 'If-Match': site.etag })
      .then(() => {
        toast.success(t.settings.saved);
        void queryClient.invalidateQueries({ queryKey: ['site', site.id] });
        void queryClient.invalidateQueries({ queryKey: ['sites'] });
        void queryClient.invalidateQueries({ queryKey: ['switcher'] });
      })
      .catch((cause: unknown) => {
        // 409 — занятое короткое имя: это ошибка поля, показывать её у него.
        if (cause instanceof ApiError && cause.status === 409) {
          setSlugError(cause.detail);
        } else {
          toast.error(t.settings.failed, { description: describe(cause) });
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['section']} onSubmit={submit} noValidate>
      <h2 className={styles['section__title']}>{t.settings.general}</h2>

      <div className={styles['card']}>
        <Field label={t.settings.name} required>
          <Input
            value={name}
            maxLength={200}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </Field>

        <Field label={t.settings.slug} required hint={t.settings.slugHint} error={slugError}>
          <Input
            value={slug}
            maxLength={200}
            spellCheck={false}
            className={styles['mono']}
            onChange={(event) => {
              setSlug(slugify(event.target.value));
            }}
          />
        </Field>

        <div className={styles['actions']}>
          <Button
            type="submit"
            loading={busy}
            disabled={readOnly || !changed || name.trim() === '' || slug === ''}
          >
            {t.settings.save}
          </Button>
        </div>
      </div>
    </form>
  );
}

function AddressCard({
  siteId,
  environment,
  readOnly,
}: {
  siteId: string;
  environment: EnvironmentBody;
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  const production = environment.kind === 'production';
  const savedOrigins = environment.extra_origins ?? [];

  const [url, setUrl] = useState(environment.url ?? '');
  const [previewUrl, setPreviewUrl] = useState(environment.preview_url ?? '');
  const [previewError, setPreviewError] = useState<string>();
  const [extra, setExtra] = useState(savedOrigins.join('\n'));
  const [urlError, setUrlError] = useState<string>();
  const [extraError, setExtraError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const origins = extra
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

  const changed =
    url.trim() !== (environment.url ?? '') ||
    previewUrl.trim() !== (environment.preview_url ?? '') ||
    origins.join('\n') !== savedOrigins.join('\n');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setUrlError(undefined);
    setPreviewError(undefined);
    setExtraError(undefined);
    setBusy(true);

    sitesSetEnvironmentAddress(
      siteId,
      environment.kind,
      { url: url.trim(), extra_origins: origins, preview_url: previewUrl.trim() },
      { 'If-Match': environment.etag },
    )
      .then(() => {
        toast.success(t.settings.saved);
        void queryClient.invalidateQueries({ queryKey: ['environments', siteId] });
        void queryClient.invalidateQueries({ queryKey: ['site', siteId] });
        void queryClient.invalidateQueries({ queryKey: ['sites'] });
      })
      .catch((cause: unknown) => {
        // Сервер говорит, какое поле не так, — ошибка показывается у него.
        if (cause instanceof ApiError && cause.status === 422) {
          const where = cause.details[0]?.location ?? '';

          if (where.includes('extra_origins')) {
            setExtraError(sentence(cause.detail));
          } else if (where.includes('preview_url')) {
            setPreviewError(sentence(cause.detail));
          } else {
            setUrlError(sentence(cause.detail));
          }

          return;
        }

        toast.error(t.settings.failed, { description: describe(cause) });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['card']} onSubmit={submit} noValidate>
      <div className={styles['card__head']}>
        <h3 className={styles['card__title']}>
          {production ? <Globe /> : <FlaskConical />}
          {production ? t.env.production : t.env.development}
        </h3>
        <Badge size="sm" tone={environment.url == null ? 'neutral' : 'success'}>
          {environment.url == null ? 'адрес не указан' : 'подключён'}
        </Badge>
      </div>

      <Field label={t.settings.url} hint={t.settings.urlHint} error={urlError}>
        <Input
          type="url"
          inputMode="url"
          spellCheck={false}
          className={styles['mono']}
          placeholder={production ? 'https://shop.ru' : 'http://localhost:3000'}
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
          }}
        />
      </Field>

      {/* У Production предпросмотр — препрод-сборка: боевой сайт черновиков не
          покажет. У Development адрес сайта и есть адрес предпросмотра. */}
      {production && (
        <Field
          label="Адрес предпросмотра"
          hint="Препрод-сборка сайта (Production с ключом предпросмотра) — её Faceta откроет рядом с формой записи. Пусто — адрес сайта."
          error={previewError}
        >
          <Input
            type="url"
            inputMode="url"
            spellCheck={false}
            className={styles['mono']}
            placeholder="https://preprod.shop.ru"
            value={previewUrl}
            onChange={(event) => {
              setPreviewUrl(event.target.value);
            }}
          />
        </Field>
      )}

      {/* Разрешённые источники нужны редко — раскрыты сами, только если
          они уже есть: иначе это шум, который заставляет думать лишний раз. */}
      <details className={styles['more']} open={savedOrigins.length > 0}>
        <summary className={styles['more__summary']}>{t.settings.more}</summary>

        <Field label={t.settings.extra} hint={t.settings.extraHint} error={extraError}>
          <Textarea
            rows={3}
            spellCheck={false}
            className={styles['mono']}
            value={extra}
            onChange={(event) => {
              setExtra(event.target.value);
            }}
          />
        </Field>
      </details>

      <div className={styles['actions']}>
        <Button type="submit" loading={busy} disabled={readOnly || !changed}>
          {t.settings.saveAddress}
        </Button>
      </div>
    </form>
  );
}

function describe(cause: unknown): string {
  return cause instanceof ApiError ? cause.detail : t.sites.unknownError;
}

/**
 * Сообщение сервера — с заглавной буквы. Сервер пишет ошибки со строчной, как
 * принято в Go (они встраиваются в другие сообщения), а под полем это
 * отдельное предложение.
 */
function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const COMMON_LANGUAGES = [
  'ru',
  'en',
  'kk',
  'uz',
  'uk',
  'be',
  'de',
  'fr',
  'es',
  'it',
  'pt-BR',
  'tr',
  'zh',
  'ja',
  'ar',
];

/**
 * Языки содержимого сайта. Первый — основной; остальные — переводы полей,
 * отмеченных «Переводимое» (docs/research/01-content-modeling.md, вывод 8).
 */
function LanguagesSection({ site, readOnly }: { site: SiteDetailBody; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [languages, setLanguages] = useState<string[]>(site.languages ?? ['ru']);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const saved = site.languages ?? ['ru'];
  const changed = languages.join(',') !== saved.join(',');
  const mainChanged = languages[0] !== saved[0];

  const add = () => {
    const code = draft.trim();

    if (code === '' || languages.includes(code)) {
      setDraft('');

      return;
    }

    setLanguages([...languages, code]);
    setDraft('');
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setBusy(true);

    sitesUpdate(site.id, { languages }, { 'If-Match': site.etag })
      .then(() => {
        toast.success(t.settings.saved);
        void queryClient.invalidateQueries({ queryKey: ['site', site.id] });
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 422) {
          setError(cause.detail);
        } else {
          toast.error(t.settings.failed, { description: describe(cause) });
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['section']} onSubmit={submit} noValidate>
      <header className={styles['section__head']}>
        <h2 className={styles['section__title']}>{t.settings.languages}</h2>
        <p className={styles['section__text']}>{t.settings.languagesText}</p>
      </header>

      <div className={styles['card']}>
        <ul className={styles['languages']}>
          {languages.map((code, index) => (
            <li key={code} className={styles['language']}>
              <span className={styles['mono']}>{code}</span>
              {index === 0 ? (
                <Badge size="sm" tone="accent">
                  {t.settings.main}
                </Badge>
              ) : (
                !readOnly && (
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    type="button"
                    onClick={() => {
                      setLanguages([code, ...languages.filter((item) => item !== code)]);
                    }}
                  >
                    {t.settings.makeMain}
                  </Button>
                )
              )}
              {!readOnly && languages.length > 1 && (
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  type="button"
                  aria-label={t.settings.removeLanguage(code)}
                  onClick={() => {
                    setLanguages(languages.filter((item) => item !== code));
                  }}
                >
                  <X size={14} />
                </Button>
              )}
            </li>
          ))}
        </ul>

        {!readOnly && (
          <div className={styles['language-add']}>
            <Field label={t.settings.addLanguage} hint={t.settings.languageCode} error={error}>
              <Input
                value={draft}
                list="site-languages"
                maxLength={6}
                spellCheck={false}
                className={styles['mono']}
                onChange={(event) => {
                  setDraft(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    add();
                  }
                }}
              />
            </Field>
            <datalist id="site-languages">
              {COMMON_LANGUAGES.filter((code) => !languages.includes(code)).map((code) => (
                <option key={code} value={code} />
              ))}
            </datalist>
            <Button type="button" variant="outline" tone="neutral" onClick={add}>
              {t.settings.addLanguage}
            </Button>
          </div>
        )}

        {mainChanged && <p className={styles['note']}>{t.settings.mainChanged}</p>}

        <div className={styles['actions']}>
          <Button type="submit" loading={busy} disabled={readOnly || !changed}>
            {t.settings.save}
          </Button>
        </div>
      </div>
    </form>
  );
}
